import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import SettingsHubPanel from '../../components/SettingsHubPanel';
import TextInput from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import Card from '../../components/ui/Card';
import { useApplicationSettings } from './useData';
import api from '../../services/api';
import { cn } from '../../lib/utils';
import type { EmbeddedInConfigurationHub } from '../configuration-settings/models';

export type GoogleSyncSettingsPageProps = EmbeddedInConfigurationHub;

type GoogleSyncFormState = {
  googleOAuthClientId: string;
  googleOAuthClientSecretSet?: boolean;
};

const GoogleSyncSettingsPage: React.FC<GoogleSyncSettingsPageProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const isAdmin = user?.role === 'ADMIN';

  const { applicationSettings, isLoading, mutate: mutateApp } = useApplicationSettings();

  const [form, setForm] = useState<GoogleSyncFormState>({ googleOAuthClientId: '' });
  const [googleOAuthClientSecret, setGoogleOAuthClientSecret] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!applicationSettings) return;
    setForm({
      googleOAuthClientId: applicationSettings.googleOAuthClientId ?? '',
      googleOAuthClientSecretSet: applicationSettings.googleOAuthClientSecretSet,
    });
  }, [applicationSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        googleOAuthClientId: form.googleOAuthClientId.trim(),
      };
      if (googleOAuthClientSecret !== '') {
        payload.googleOAuthClientSecret = googleOAuthClientSecret;
      }
      await api.patch('/settings/application', payload);
      setGoogleOAuthClientSecret('');
      await mutateApp();
      showSuccess('Google Sync settings saved');
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to save Google Sync settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    if (embedded) {
      return (
        <Card variant="outlined" className="p-6 text-center">
          <p className={cn('text-sm', colors.text)}>Google Sync settings are only available to administrators.</p>
        </Card>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className={cn('mb-2 text-2xl font-bold', colors.text)}>Access denied</h2>
          <p className={cn('text-sm', colors.textMuted)}>This page is only available to administrators.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    if (embedded) {
      return (
        <div className="py-10">
          <LoadingSpinner message="Loading settings…" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Loading settings…" size="large" />
      </div>
    );
  }

  const saveFooter = (
    <div className="flex justify-end">
      <Button type="button" variant="default" loading={saving} disabled={saving} onClick={() => void saveSettings()}>
        Save Google Sync settings
      </Button>
    </div>
  );

  const formFields = (
    <div className="space-y-4">
      <p className={cn('text-sm', colors.textMuted)}>
        OAuth credentials for Gmail account sign-in (XOAUTH2) and Google Calendar sync. Users connect their own Google
        accounts from Preferences after these server credentials are saved.
      </p>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <Label htmlFor="googleOAuthClientId">Client ID</Label>
          <TextInput
            id="googleOAuthClientId"
            name="googleOAuthClientId"
            value={form.googleOAuthClientId}
            onChange={(e) => setForm((f) => ({ ...f, googleOAuthClientId: e.target.value }))}
            placeholder="your-client-id.apps.googleusercontent.com"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="googleOAuthClientSecret">Client secret</Label>
          <TextInput
            id="googleOAuthClientSecret"
            name="googleOAuthClientSecret"
            type="password"
            value={googleOAuthClientSecret}
            onChange={(e) => setGoogleOAuthClientSecret(e.target.value)}
            placeholder={form.googleOAuthClientSecretSet ? 'Leave blank to keep current' : 'Enter client secret'}
            className="mt-1"
          />
        </div>
      </div>
      <div className={cn('rounded-lg border px-3 py-3 text-xs', colors.border, colors.textMuted)}>
        <p className={cn('mb-2 font-medium', colors.text)}>Google Cloud Console setup</p>
        <ul className="list-disc space-y-1 pl-4">
          <li>Enable the Google Calendar API.</li>
          <li>
            Add redirect URIs on your backend URL for{' '}
            <code className="text-xs">/email-accounts/oauth/google/callback</code> and{' '}
            <code className="text-xs">/calendar/oauth/google/callback</code>.
          </li>
          <li>Users enable calendar sync under Preferences → Google Calendar.</li>
        </ul>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <SettingsHubPanel
        title="Google Sync"
        description="Google OAuth credentials for Gmail and Google Calendar integration."
        helpItems={[
          {
            summary: 'User connections',
            content: (
              <p>
                After saving credentials here, each user connects their Google account from{' '}
                <strong className={cn('font-medium', colors.text)}>Preferences → Google Calendar</strong> or when
                linking a Gmail mailbox.
              </p>
            ),
          },
        ]}
        footer={saveFooter}
        contentClassName="space-y-4"
      >
        {formFields}
      </SettingsHubPanel>
    );
  }

  const standaloneBody = (
    <div className="space-y-6">
      {formFields}
      <div className={cn('flex justify-end border-t pt-2', colors.border)}>{saveFooter}</div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <div>
          <h1 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>Google Sync</h1>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            Google OAuth credentials for Gmail and Google Calendar integration.
          </p>
        </div>
        {standaloneBody}
      </div>
    </div>
  );
};

export default GoogleSyncSettingsPage;
